<template>
  <div v-if="$apollo.loading">
    <spinner />
  </div>
  <div v-else class="organization-users">
    <div class="organization-users__header">
      <div class="organization-users__title">{{ adminText('organizationAll.users_57f2b181', 'Users') }}</div>
      <main-button
        v-if="canInviteUsers"
        color="primary"
        depressed
        class="organization-users__add-button"
        @click="openCreateModal"
      >
        <v-icon left size="18">add</v-icon>
        {{ adminText('organizationAll.add_user_87439385', 'Add User') }}
      </main-button>
    </div>

    <div class="organization-users__toolbar">
      <v-btn
        class="organization-users__filters-button"
        depressed
        :class="{ 'organization-users__filters-button--active': hasActiveFilters }"
        @click="openFiltersModal"
      >
        <v-icon left size="18" color="#0277BD">filter_list</v-icon>
        {{ organizationText(filtersButtonLabel) }}
        <span
          v-if="hasActiveFilters"
          class="organization-users__filters-clear"
          @click.stop="resetFilters"
        >
          <v-icon size="16" color="#0277BD">close</v-icon>
        </span>
      </v-btn>

      <v-text-field
        v-model.trim="searchQuery"
        solo
        flat
        hide-details
        prepend-inner-icon="search"
        class="organization-users__search"
        :placeholder="adminText('organizationAll.quick_search_3c4cdb32', 'Quick search')"
      />
    </div>

    <v-card class="organization-users__table" flat>
      <div class="organization-users__table-head">
        <div>{{ adminText('organizationAll.user_name_542fc714', 'User name') }}</div>
        <div>{{ adminText('organizationAll.role_c3f104d1', 'Role') }}</div>
        <div>{{ adminText('organizationAll.events_c5497bca', 'Events') }}</div>
        <div />
      </div>

      <div v-if="filteredRows.length">
        <div
          v-for="row in filteredRows"
          :key="row.key"
          class="organization-users__row"
          :class="rowClassObject(row)"
        >
          <div class="organization-users__cell organization-users__name-cell">
            {{ row.displayName }}
          </div>

          <div class="organization-users__cell">
            {{ organizationText(row.displayRole) }}
          </div>

          <div class="organization-users__cell organization-users__events-cell">
            <template v-if="row.type === 'invite'">
              <button
                type="button"
                class="organization-users__status-button"
                :class="row.statusClass"
                @click="openInviteDetails(row.raw)"
              >
                <v-icon size="18" :color="row.statusColor">{{ row.statusIcon }}</v-icon>
                <span>{{ row.eventsLabel }}</span>
              </button>
            </template>
            <template v-else>
              {{ row.eventsLabel }}
            </template>
          </div>

          <div class="organization-users__cell organization-users__actions-cell">
            <template v-if="row.actions.length">
              <v-btn
                v-for="action in row.actions"
                :key="action.icon"
                icon
                small
                class="organization-users__action-button"
                @click="action.handler()"
              >
                <v-icon :color="action.color" size="18">{{ action.icon }}</v-icon>
              </v-btn>
            </template>
          </div>
        </div>
      </div>

      <div v-else class="organization-users__empty-state">{{ adminText('organizationAll.no_users_match_the_selected_filters_94545990', 'No users match the selected filters.') }}</div>
    </v-card>

    <v-dialog v-model="isFiltersModalVisible" max-width="360">
      <v-card class="organization-users__filters-dialog">
        <div class="organization-users__filters-dialog-header">
          <div>
            <div class="organization-users__filters-title">{{ adminText('organizationAll.filters_96e57821', 'Filters') }}</div>
            <div class="organization-users__filters-subtitle">
              {{ adminText('organizationAll.narrow_users_by_role_and_invite_status_ef386415', 'Narrow users by role and invite status.') }}
            </div>
          </div>
          <v-btn icon @click="isFiltersModalVisible = false">
            <v-icon color="#666666">close</v-icon>
          </v-btn>
        </div>

        <div class="organization-users__filters-section">
          <div class="organization-users__filters-section-title">{{ adminText('organizationAll.roles_47dcc27d', 'Roles') }}</div>
          <v-checkbox
            v-for="role in roleOptions"
            :key="role.value"
            v-model="filterDraft.roles"
            :label="organizationText(role.label)"
            :value="role.value"
            hide-details
            color="primary"
            class="organization-users__checkbox"
          />
        </div>

        <div class="organization-users__filters-section">
          <div class="organization-users__filters-section-title">{{ adminText('organizationAll.status_bae7d5be', 'Status') }}</div>
          <v-checkbox
            v-for="status in statusOptions"
            :key="status.value"
            v-model="filterDraft.statuses"
            :label="organizationText(status.label)"
            :value="status.value"
            hide-details
            color="primary"
            class="organization-users__checkbox"
          />
        </div>

        <div class="organization-users__filters-actions">
          <main-button flat color="primary" @click="resetFilters">{{ adminText('organizationAll.remove_filters_1fa0c6e8', 'Remove filters') }}</main-button>
          <main-button color="primary" depressed @click="applyFilters">{{ adminText('organizationAll.apply_cfea419c', 'Apply') }}</main-button>
        </div>
      </v-card>
    </v-dialog>

    <organization-user-modal
      v-if="isInviteModalVisible"
      :initial-values="form"
      :organization-events="organizationEvents"
      :organization-event-templates="eventTemplates"
      :is-theater-organization="isTheaterOrganization"
      :vs="$v"
      type="CREATE"
      @submit="sendUserInvite()"
      @closeModal="closeModal()"
    />
    <organization-user-modal
      v-if="isUpdateUserModalVisible"
      :initial-values="form"
      :organization-events="organizationEvents"
      :organization-event-templates="eventTemplates"
      :is-theater-organization="isTheaterOrganization"
      :vs="$v"
      type="UPDATE"
      @submit="updateUserRole()"
      @delete="deleteUser()"
      @closeModal="closeModal()"
    />
    <organization-user-modal
      v-if="isInviteSentModalVisible"
      :initial-values="form"
      :organization-events="organizationEvents"
      :organization-event-templates="eventTemplates"
      :is-theater-organization="isTheaterOrganization"
      :vs="$v"
      type="SENT"
      @submit="resendInvite()"
      @delete="deleteInvite()"
      @closeModal="closeModal()"
    />
    <organization-user-modal
      v-if="isInviteExpiredModalVisible"
      :initial-values="form"
      :organization-events="organizationEvents"
      :organization-event-templates="eventTemplates"
      :is-theater-organization="isTheaterOrganization"
      :vs="$v"
      type="EXPIRED"
      @submit="resendInvite()"
      @delete="deleteInvite()"
      @closeModal="closeModal()"
    />
    <simple-snackbar v-bind.sync="snackbar" :message-prop="organizationText(snackbar &amp;&amp; (snackbar.messageProp || snackbar.message))" />
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import { required, email } from 'vuelidate/lib/validators'
import { omit, startCase } from 'lodash'

import { GET_ORGANIZATION_USERS_AND_INVITES } from '../../../graphql/organization.graphql'
import { GET_ORGANIZATION_EVENT_TEMPLATES } from '../../../graphql/eventTemplate.graphql'
import { OrganizationType } from '@/types'
import {
  UPDATE_USER_ORGANIZATION_ROLE,
  DELETE_USER_ORGANIZATION,
} from '../../../graphql/userOrganization.graphql'
import { GET_LOGGED_USER } from '../../../graphql/user.graphql'
import {
  INVITE_USER_TO_ORGANIZATION,
  DELETE_INVITE,
  RESEND_INVITE,
} from '../../../graphql/invite.graphql'

import organizationUserModal from './organization-user-modal.vue'
import simpleSnackbar from '../../shared/snackbars/simple-snackbar.vue'
import spinner from '../../shared/spinner.vue'
import { submitGuard } from '../../../utils'
import { getAuthorization } from '../../../http'

const ROLE_OPTIONS = [
  { label: 'Org. Owner', value: 'OWNER' },
  { label: 'Org. Admin', value: 'ADMIN' },
]

const STATUS_OPTIONS = [
  { label: 'Accepted', value: 'accepted' },
  { label: 'Pending', value: 'pending' },
  { label: 'Expired', value: 'expired' },
]

export default {
  mixins: [OrganizationLocalization],
  name: 'organization-users',
  components: { organizationUserModal, simpleSnackbar, spinner },
  props: {
    organizationSlug: {
      type: String,
    },
    loadEventTemplates: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      me: null,
      organizationRole: null,
      users: [],
      invites: [],
      organizationEvents: [],
      eventTemplates: [],
      organizationType: null,
      OrganizationType,
      form: {},
      snackbar: {},
      searchQuery: '',
      activeFilters: {
        roles: [],
        statuses: [],
      },
      filterDraft: {
        roles: [],
        statuses: [],
      },
      isFiltersModalVisible: false,
      isInviteModalVisible: false,
      isUpdateUserModalVisible: false,
      isInviteSentModalVisible: false,
      isInviteExpiredModalVisible: false,
    }
  },
  validations() {
    return {
      form: {
        email: { required, email },
        role: { required },
        eventIds: {
          requiredForEventScopedRole: value => {
            if (this.organizationType === OrganizationType.THEATER) {
              return true
            }
            if (!['EVENT_ADMIN', 'EVENT_VIEWER'].includes(this.form.role)) {
              return true
            }

            return Array.isArray(value) && value.length > 0
          },
        },
        eventTemplateIds: {
          requiredForTheaterScopedRole: value => {
            if (this.organizationType !== OrganizationType.THEATER) {
              return true
            }
            if (!['EVENT_ADMIN', 'EVENT_VIEWER'].includes(this.form.role)) {
              return true
            }

            return Array.isArray(value) && value.length > 0
          },
        },
      },
    }
  },
  computed: {
    canInviteUsers() {
      return ['OWNER', 'ADMIN'].includes(this.organizationRole)
    },
    canEditUsers() {
      return this.organizationRole === 'OWNER'
    },
    roleOptions() {
      return ROLE_OPTIONS
    },
    statusOptions() {
      return STATUS_OPTIONS
    },
    activeFiltersCount() {
      return this.activeFilters.roles.length + this.activeFilters.statuses.length
    },
    hasActiveFilters() {
      return this.activeFiltersCount > 0
    },
    isTheaterOrganization() {
      return this.organizationType === OrganizationType.THEATER
    },
    filtersButtonLabel() {
      if (!this.activeFiltersCount) {
        return 'Filters'
      }

      if (this.activeFiltersCount === 1) {
        return '1 Filter'
      }

      return this.activeFiltersCount >= 2 && this.activeFiltersCount <= 4
        ? this.adminText('organizationAll.count_filters_de9ccab6', '{{count}} Filters', { count: this.activeFiltersCount })
        : this.adminText('organizationAll.filterCountMany', '{{count}} Filters', { count: this.activeFiltersCount })
    },
    tableRows() {
      const acceptedUsers = this.users.map(user => ({
        key: `user-${user.id}`,
        raw: user,
        type: 'user',
        filterRole: this.normalizeRole(user.organizationRole),
        filterStatus: 'accepted',
        displayName: this.getUserDisplayName(user),
        displayRole: this.formatRole(user.organizationRole),
        eventsLabel: this.getUserEventsLabel(user),
        statusClass: '',
        statusColor: '#666666',
        statusIcon: '',
        actions: this.getUserActions(user),
      }))

      const inviteRows = this.invites.map(invite => {
        const isExpired = Boolean(invite.expired)

        return {
          key: `invite-${invite.id}`,
          raw: invite,
          type: 'invite',
          filterRole: this.normalizeRole(invite.organizationRole),
          filterStatus: isExpired ? 'expired' : 'pending',
          displayName: invite.email,
          displayRole: this.formatRole(invite.organizationRole),
          eventsLabel: isExpired ? this.adminText('organizationAll.invite_expired_bbcffd2e', 'Invite expired') : this.adminText('organizationAll.invite_sent_c484ac06', 'Invite sent'),
          statusClass: isExpired
            ? 'organization-users__status-button--expired'
            : 'organization-users__status-button--pending',
          statusColor: isExpired ? '#DB384C' : '#55A35D',
          statusIcon: isExpired ? 'error' : 'mark_email_read',
          actions: this.getInviteActions(invite),
        }
      })

      return [...acceptedUsers, ...inviteRows]
    },
    filteredRows() {
      const query = this.searchQuery.toLowerCase()

      return this.tableRows.filter(row => {
        const matchesQuery =
          !query ||
          row.displayName.toLowerCase().includes(query) ||
          row.displayRole.toLowerCase().includes(query) ||
          row.eventsLabel.toLowerCase().includes(query)

        const matchesRole =
          !this.activeFilters.roles.length || this.activeFilters.roles.includes(row.filterRole)

        const matchesStatus =
          !this.activeFilters.statuses.length ||
          this.activeFilters.statuses.includes(row.filterStatus)

        return matchesQuery && matchesRole && matchesStatus
      })
    },
  },
  methods: {
    normalizeRole(role) {
      return String(role || '')
        .trim()
        .replace(/\s+/g, '_')
        .replace(/\./g, '')
        .toUpperCase()
    },
    formatRole(role) {
      const normalizedRole = this.normalizeRole(role)
      const labelMap = {
        OWNER: this.adminText('organizationAll.org_owner_3d58fb5a', 'Org. Owner'),
        ADMIN: this.adminText('organizationAll.org_admin_495869db', 'Org. Admin'),
        EVENT_ADMIN: this.adminText('organizationAll.event_admin_e58020f5', 'Event admin'),
        EVENT_VIEWER: this.adminText('organizationAll.event_viewer_2533fdfb', 'Event viewer'),
      }

      return labelMap[normalizedRole] || startCase(String(role || '').toLowerCase())
    },
    getUserDisplayName(user) {
      const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
      const fallback = user.email || this.organizationText('Unknown user')

      if (this.me && user.id === this.me.id) {
        return `${fullName || fallback} (${this.adminText('organizationAll.you_8af56de6', 'you')})`
      }

      return fullName || fallback
    },
    getUserEventsLabel(user) {
      const normalizedRole = this.normalizeRole(user.organizationRole)
      const isEventScopedRole = ['EVENT_ADMIN', 'EVENT_VIEWER'].includes(normalizedRole)

      if (!isEventScopedRole) {
        return this.adminText('organizationAll.all_6a720856', 'All')
      }

      if (this.isTheaterOrganization) {
        const titles = (user.userEventTemplates || [])
          .map(ut => ut.eventTemplate && ut.eventTemplate.title)
          .filter(Boolean)

        return titles.length ? titles.join(', ') : this.adminText('organizationAll.no_templates_assigned_b08b6e50', 'No templates assigned')
      }

      const eventTitles = (user.events || []).map(event => event.title).filter(Boolean)

      return eventTitles.length ? eventTitles.join(', ') : this.adminText('organizationAll.no_events_assigned_d169d719', 'No events assigned')
    },
    getUserActions(user) {
      if (!this.canEditUsers) return []
      if (this.me && user.id === this.me.id) return []

      return [
        {
          icon: 'edit',
          color: '#0277BD',
          handler: () => this.openEditUserModal(user),
        },
      ]
    },
    getInviteActions(invite) {
      if (!this.canInviteUsers) return []

      const actions = [
        {
          icon: 'edit',
          color: '#0277BD',
          handler: () => this.openInviteDetails(invite),
        },
      ]

      if (invite.expired) {
        actions.push({
          icon: 'refresh',
          color: '#0277BD',
          handler: () => {
            this.form = { email: invite.email, role: invite.organizationRole, id: invite.id }
            this.resendInvite()
          },
        })
      }

      actions.push({
        icon: 'delete',
        color: '#DB384C',
        handler: () => {
          this.form = { email: invite.email, role: invite.organizationRole, id: invite.id }
          this.deleteInvite()
        },
      })

      return actions
    },
    rowClassObject(row) {
      return {
        'organization-users__row--pending': row.filterStatus === 'pending',
        'organization-users__row--expired': row.filterStatus === 'expired',
      }
    },
    openFiltersModal() {
      this.filterDraft = {
        roles: [...this.activeFilters.roles],
        statuses: [...this.activeFilters.statuses],
      }
      this.isFiltersModalVisible = true
    },
    applyFilters() {
      this.activeFilters = {
        roles: [...this.filterDraft.roles],
        statuses: [...this.filterDraft.statuses],
      }
      this.isFiltersModalVisible = false
    },
    resetFilters() {
      this.filterDraft = { roles: [], statuses: [] }
      this.activeFilters = { roles: [], statuses: [] }
      this.isFiltersModalVisible = false
    },
    openCreateModal() {
      this.form = { eventIds: [], eventTemplateIds: [] }
      this.isInviteModalVisible = true
    },
    normalizeEventIds(eventIds = []) {
      return eventIds
        .map(eventId => Number(eventId))
        .filter(eventId => Number.isInteger(eventId))
    },
    normalizedFormValues(values = {}) {
      return {
        ...values,
        eventIds: this.normalizeEventIds(values.eventIds || []),
        eventTemplateIds: this.normalizeEventIds(values.eventTemplateIds || []),
      }
    },
    normalizedInvitePayload(form) {
      const base = this.normalizedFormValues(form)
      if (this.isTheaterOrganization) {
        return {
          email: base.email,
          role: base.role,
          eventTemplateIds: base.eventTemplateIds,
        }
      }

      return {
        email: base.email,
        role: base.role,
        eventIds: base.eventIds,
      }
    },
    openEditUserModal(user) {
      this.form = {
        ...omit(user, ['organizationRole', 'events', 'userEventTemplates']),
        role: user.organizationRole,
        eventIds: (user.events || []).map(event => event.id),
        eventTemplateIds: (user.userEventTemplates || []).map(ut => Number(ut.eventTemplateId)),
      }
      this.isUpdateUserModalVisible = true
    },
    openSentInviteInfoModal(invite) {
      this.form = {
        email: invite.email,
        role: invite.organizationRole,
        id: invite.id,
        eventIds: invite.selectedEventIds || [],
        eventTemplateIds: invite.selectedEventTemplateIds || [],
      }
      this.isInviteSentModalVisible = true
    },
    openExpiredInviteModal(invite) {
      this.form = {
        email: invite.email,
        role: invite.organizationRole,
        id: invite.id,
        eventIds: invite.selectedEventIds || [],
        eventTemplateIds: invite.selectedEventTemplateIds || [],
      }
      this.isInviteExpiredModalVisible = true
    },
    openInviteDetails(invite) {
      if (invite.expired) {
        this.openExpiredInviteModal(invite)
      } else {
        this.openSentInviteInfoModal(invite)
      }
    },
    async closeModal() {
      this.isInviteModalVisible = false
      this.isUpdateUserModalVisible = false
      this.isInviteSentModalVisible = false
      this.isInviteExpiredModalVisible = false
      this.form = {}
      this.$v.$reset()
    },
    async refreshUsersList() {
      const organizationUsersQuery = this.$apollo.queries.getOrganizationUsers

      if (organizationUsersQuery) {
        await organizationUsersQuery.refetch()
      }
    },
    async sendUserInvite() {
      this.$v.form.$touch()

      if (this.$v.form.$invalid === true) {
        this.snackbar = {
          open: true,
          messageProp: 'Fill in the required information.',
          color: 'error',
        }
      } else {
        await submitGuard(this, async () => {
          const { errors } = await this.$apollo.mutate({
            mutation: INVITE_USER_TO_ORGANIZATION,
            variables: {
              organizationSlug: this.organizationSlug,
              inviteData: this.normalizedInvitePayload(this.form),
            },
            awaitRefetchQueries: true,
            refetchQueries: [
              {
                query: GET_ORGANIZATION_USERS_AND_INVITES,
                variables: { organizationSlug: this.organizationSlug },
              },
            ],
          })

          if (errors) {
            this.snackbar = {
              open: true,
              messageProp: errors[0].message,
              color: 'error',
            }
          } else {
            await this.refreshUsersList()
            this.snackbar = {
              open: true,
              messageProp: 'Successfully sent invite!',
              color: 'success',
            }
            this.closeModal()
          }
        })
      }
    },
    async updateUserRole() {
      this.$v.form.$touch()

      if (this.$v.form.$invalid === true) {
        this.snackbar = {
          open: true,
          messageProp: 'Fill in the required information.',
          color: 'error',
        }
      } else {
        await submitGuard(this, async () => {
          const { errors } = await this.$apollo.mutate({
            mutation: UPDATE_USER_ORGANIZATION_ROLE,
            variables: {
              organizationSlug: this.organizationSlug,
              email: this.form.email,
              role: this.form.role,
              eventIds: this.isTheaterOrganization ? null : this.normalizeEventIds(this.form.eventIds),
              eventTemplateIds: this.isTheaterOrganization
                ? this.normalizeEventIds(this.form.eventTemplateIds)
                : null,
            },
            awaitRefetchQueries: true,
            refetchQueries: [
              {
                query: GET_ORGANIZATION_USERS_AND_INVITES,
                variables: { organizationSlug: this.organizationSlug },
              },
            ],
          })

          if (errors) {
            this.snackbar = {
              open: true,
              messageProp: errors[0].message,
              color: 'error',
            }
          } else {
            await this.refreshUsersList()
            this.snackbar = {
              open: true,
              messageProp: 'Successfully updated user!',
              color: 'success',
            }
            this.closeModal()
          }
        })
      }
    },
    async deleteUser() {
      const { errors } = await this.$apollo.mutate({
        mutation: DELETE_USER_ORGANIZATION,
        variables: {
          organizationSlug: this.organizationSlug,
          email: this.form.email,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: GET_ORGANIZATION_USERS_AND_INVITES,
            variables: { organizationSlug: this.organizationSlug },
          },
        ],
      })

      if (errors) {
        this.snackbar = {
          open: true,
          messageProp: errors[0].message,
          color: 'error',
        }
      } else {
        await this.refreshUsersList()
        this.snackbar = {
          open: true,
          messageProp: 'Successfully deleted user!',
          color: 'success',
        }
        this.closeModal()
      }
    },
    async resendInvite() {
      const { errors } = await this.$apollo.mutate({
        mutation: RESEND_INVITE,
        variables: {
          organizationSlug: this.organizationSlug,
          inviteId: this.form.id,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: GET_ORGANIZATION_USERS_AND_INVITES,
            variables: { organizationSlug: this.organizationSlug },
          },
        ],
      })

      if (errors) {
        this.snackbar = {
          open: true,
          messageProp: errors[0].message,
          color: 'error',
        }
      } else {
        await this.refreshUsersList()
        this.snackbar = {
          open: true,
          messageProp: 'Successfully resent invite!',
          color: 'success',
        }
        this.closeModal()
      }
    },
    async deleteInvite() {
      const { errors } = await this.$apollo.mutate({
        mutation: DELETE_INVITE,
        variables: {
          organizationSlug: this.organizationSlug,
          inviteId: this.form.id,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: GET_ORGANIZATION_USERS_AND_INVITES,
            variables: { organizationSlug: this.organizationSlug },
          },
        ],
      })

      if (errors) {
        this.snackbar = {
          open: true,
          messageProp: errors[0].message,
          color: 'error',
        }
      } else {
        await this.refreshUsersList()
        this.snackbar = {
          open: true,
          messageProp: 'Successfully deleted invite!',
          color: 'success',
        }
        this.closeModal()
      }
    },
  },
  apollo: {
    getOrganizationUsers: {
      query: GET_ORGANIZATION_USERS_AND_INVITES,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
        }
      },
      result({ data, loading }) {
        if (!loading && data) {
          this.organizationEvents = data.getOrganization.events || []
          this.organizationType = data.getOrganization.type || null
          this.users = data.getOrganizationUsers
          this.invites = data.getOrganizationInvites
        }
      },
    },
    getOrganizationEventTemplates: {
      query: GET_ORGANIZATION_EVENT_TEMPLATES,
      skip() {
        return !this.loadEventTemplates
      },
      variables() {
        return {
          organizationSlug: this.organizationSlug,
        }
      },
      result({ data, loading }) {
        if (!loading && data) {
          this.eventTemplates = data.getOrganizationEventTemplates || []
        }
      },
    },
    me: {
      query: GET_LOGGED_USER,
      async result({ data, loading }) {
        if (!loading && data) {
          this.me = data.me
          const auth = await getAuthorization(null, this.organizationSlug, null)
          this.organizationRole = auth.organizationRole
        }
      },
    },
  },
}
</script>

<style scoped>
.organization-users {
  max-width: 1038px;
}

.organization-users__header {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
}

.organization-users__title {
  font-family: 'Raleway';
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.03em;
  color: #ff4c00;
}

.organization-users__add-button {
  max-width: 150px;
}

.organization-users__toolbar {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px;
  margin-bottom: 0.125rem;
}

.organization-users__filters-button {
  min-width: 116px !important;
  height: 48px !important;
  margin: 0 !important;
  border-radius: 8px 0 0 0;
  background: #f0f6fa !important;
  box-shadow: none !important;
  color: #0277bd !important;
  font-family: 'Raleway';
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: -0.015em;
  text-transform: uppercase;
}

.organization-users__filters-button--active {
  background: #e6f1f8 !important;
}

.organization-users__filters-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 0 0 0.125rem !important;
  width: 20px;
  height: 20px;
  border-radius: 50%;
}

.organization-users__filters-clear:hover {
  background: rgba(2, 119, 189, 0.12);
}

.organization-users__search {
  margin-top: 0 !important;
}

.organization-users__search >>> .v-input__slot {
  min-height: 48px !important;
  margin-bottom: 0 !important;
  border-radius: 0 8px 0 0 !important;
  background: #f5f5f5 !important;
  box-shadow: none !important;
}

.organization-users__search >>> input,
.organization-users__search >>> .v-label {
  font-family: 'Source Sans Pro';
  font-size: 1rem;
  color: #666666;
}

.organization-users__table {
  overflow: hidden;
  border: 1px solid #f5f5f5;
  border-radius: 0 0 8px 8px;
  box-shadow: none !important;
}

.organization-users__table-head,
.organization-users__row {
  display: grid;
  grid-template-columns: minmax(0, 218px) minmax(0, 120px) minmax(0, 1fr) 136px;
  gap: 1rem;
  align-items: center;
}

.organization-users__table-head {
  min-height: 48px;
  padding: 0 1.5rem 0 1.5rem;
  background: #f5f5f5;
  font-family: 'Raleway';
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.015em;
  color: #666666;
}

.organization-users__row {
  min-height: 56px;
  padding: 0.8125rem 0.75rem 0.8125rem 1.5rem;
  border-top: 1px solid #f5f5f5;
  background: white;
  transition: background-color 0.2s ease;
}

.organization-users__row:hover {
  background: #f0f6fa;
}

.organization-users__row--pending:hover {
  background: #eef6ef;
}

.organization-users__row--expired:hover {
  background: #fbebed;
}

.organization-users__cell {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: 'Source Sans Pro';
  font-size: 1rem;
  line-height: 1.5;
  color: #666666;
}

.organization-users__row--pending .organization-users__cell,
.organization-users__row--pending .organization-users__status-button {
  color: #55a35d;
}

.organization-users__row--expired .organization-users__cell,
.organization-users__row--expired .organization-users__status-button {
  color: #db384c;
}

.organization-users__events-cell {
  overflow: hidden;
}

.organization-users__status-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  max-width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  font-family: 'Source Sans Pro';
  font-size: 1rem;
  line-height: 1.5;
  cursor: pointer;
}

.organization-users__status-button span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.organization-users__status-button--pending {
  color: #55a35d;
}

.organization-users__status-button--expired {
  color: #db384c;
}

.organization-users__actions-cell {
  display: flex;
  justify-content: flex-end;
  gap: 0.25rem;
  overflow: visible;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

.organization-users__row:hover .organization-users__actions-cell {
  opacity: 1;
  pointer-events: auto;
}

.organization-users__action-button {
  width: 36px;
  height: 36px;
  background: white !important;
}

.organization-users__empty-state {
  padding: 2rem 1.5rem;
  font-family: 'Source Sans Pro';
  font-size: 1rem;
  color: #979797;
}

.organization-users__filters-dialog {
  border-radius: 12px;
  padding: 1.25rem;
}

.organization-users__filters-dialog-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.organization-users__filters-title {
  font-family: 'Raleway';
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.2;
  color: #0277bd;
}

.organization-users__filters-subtitle {
  margin-top: 0.5rem;
  font-family: 'Source Sans Pro';
  font-size: 1rem;
  line-height: 1.5;
  color: #979797;
}

.organization-users__filters-section + .organization-users__filters-section {
  margin-top: 1rem;
}

.organization-users__filters-section-title {
  margin-bottom: 0.5rem;
  font-family: 'Raleway';
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.2;
  color: #666666;
}

.organization-users__checkbox {
  margin-top: 0.75rem;
  padding-top: 0;
}

.organization-users__filters-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
}

@media screen and (max-width: 900px) {
  .organization-users__table-head,
  .organization-users__row {
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1.2fr) 112px;
  }
}

@media screen and (max-width: 700px) {
  .organization-users__toolbar {
    grid-template-columns: 1fr;
  }

  .organization-users__filters-button {
    width: 100%;
    border-radius: 8px;
  }

  .organization-users__search >>> .v-input__slot {
    border-radius: 8px !important;
  }

  .organization-users__table {
    overflow-x: auto;
  }

  .organization-users__table-head,
  .organization-users__row {
    min-width: 720px;
  }

  .organization-users__actions-cell {
    opacity: 1;
    pointer-events: auto;
  }
}
</style>
